<script setup lang="ts">
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import {
  activeCatalogue,
  catalogues,
  toggleCatalogue,
} from "@/stores/CatalogueTabsState";

/**
 * Onglets des catalogues de la légende, dans l'en-tête. Toucher l'onglet ouvert
 * le referme (la ligne des notes se replie). La bande `.catalogue-strip` garde
 * sa largeur naturelle : l'en-tête la mesure pour savoir si les onglets
 * tiennent sur la 1re ligne (cf. useCanvasHeaderLayout).
 */
const emit = defineEmits<{ (e: "help"): void }>();
</script>

<template>
  <div
    class="catalogue-tabs"
    role="tablist"
    :aria-label="$t('canvas:header.catalogues.label')"
  >
    <div class="catalogue-strip">
      <button
        v-for="name in catalogues.keys()"
        :key="name"
        type="button"
        role="tab"
        class="catalogue-tab"
        :class="{ active: activeCatalogue === name }"
        :aria-selected="activeCatalogue === name"
        @click="toggleCatalogue(name)"
      >
        {{ name }}
      </button>
      <button
        type="button"
        class="catalogue-tab catalogue-help"
        :aria-label="$t('canvas:header.catalogues.help')"
        @click="emit('help')"
      >
        <PhosphorIcon name="question" :size="13" weight="bold" />
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.catalogue-tabs {
  height: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

// Largeur naturelle : c'est elle que mesure l'en-tête.
.catalogue-strip {
  display: flex;
  align-items: stretch;
  width: max-content;
  height: 100%;
  padding: 0 4px;
}

.catalogue-tab {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 11px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.65);
  font-size: 12px;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
  transition:
    color 0.15s,
    background 0.15s;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
    background: rgba(255, 255, 255, 0.05);
  }

  &.active {
    color: #fff;
    font-weight: 500;
    background: rgba(255, 255, 255, 0.06);
    box-shadow: inset 0 -2px 0 0 var(--bs-primary);
  }
}

.catalogue-help {
  justify-content: center;
  padding: 0 8px;
  color: rgba(255, 255, 255, 0.5);
}
</style>
